import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  RefreshCw,
  Download,
  FolderOpen,
  ChevronDown,
  FileSpreadsheet,
  Link2Off,
  Loader2,
  ShieldAlert
} from 'lucide-react';
import { PostClosingAnalyticsCards } from './PostClosingAnalyticsCards';
import { ProspectFiltersComponent } from './ProspectFilters';
import { PostClosingTable } from './PostClosingTable';
import { LoanDetailsSheet } from './LoanDetailsSheet';
import { usePaginatedProspects, fetchAllProspects, type FetchMode } from './use-paginated-prospects';
import { useServerAnalytics } from './use-server-analytics';
import { usePostClosingAnalytics } from './use-post-closing-analytics';
import { useAvailableFolders } from './use-available-folders';
import { ProspectFilters, ProspectSortConfig, ProspectLoan } from './types';
import type { PostClosingQueueStage } from './postClosingQueueStage';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';
import { useAuth } from '@/contexts/useAuth';
import { isExtendedUser } from '@/types/auth';
import { getInvestorLockDocuments, getDocumentDownloadUrl } from '@/services/loanDocumentService';
import { LoanCenterRedesign } from './redesign/LoanCenterRedesign';
import { DirectionTabs } from './redesign/primitives/DirectionTabs';
import { useLoanCenterDirection, type LoanCenterDirection } from './redesign/use-loan-center-direction';
import { supabase } from '@/utils/supabase';
import { parseLoanCenterDeepLink, type LoanDetailsTab } from './loanCenterDeepLink';
import {
  addLoanDataExportCandidates,
  clearLoanDataExportCandidates,
  setLoanDataExportSelectionOwner
} from '@/stores/loanDataExportSelectionStore';
import type { LoanDataExportCandidate } from '@/types/loan-data-export';
import {
  isEncompassLinkMissingError,
  isLoanAccessDeniedError,
  useLoanCenterScope
} from './loanScope';

const log = createComponentLogger('ProspectDashboard', 'encompass-prospects');

const DEFAULT_EXCLUDED_FOLDERS = [
  'Test',
  '(Archive)',
  'Transferred Data',
  'Completed Loans',
  'Adverse Loans',
  '(Trash)',
  'Prospect'
];

// MERS Transfer Date Audit scans ALL purchased loans, which live in the
// 'Completed Loans' folder — so it must NOT exclude that folder. It still
// excludes non-operational/system folders (Test, archives, trash, prospects).
const MERS_AUDIT_EXCLUDED_FOLDERS = DEFAULT_EXCLUDED_FOLDERS.filter(
  f => f !== 'Completed Loans'
);

// CSV escaper: null/undefined → '', double any internal quotes, always wrap.
const csvCell = (value: unknown): string =>
  `"${value === null || value === undefined ? '' : String(value).replace(/"/g, '""')}"`;

// Calendar-date portion of an ISO date/timestamp ('' when missing); no TZ math.
const csvDate = (value?: string | null): string => (value ? String(value).slice(0, 10) : '');

const downloadCsv = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

interface ProspectDashboardProps {
  onViewDocuments?: (loan: ProspectLoan) => void;
  onOpenDataExports?: () => void;
  canGenerateReports?: boolean;
}

function toExportCandidate(loan: ProspectLoan): LoanDataExportCandidate {
  return {
    id: loan.id,
    encompassLoanId: loan.encompass_loan_id,
    loanNumber: loan.trans_details_loan_number || '',
    borrowerFirstName: loan.borrower_first_name ?? null,
    borrowerLastName: loan.borrower_last_name ?? null,
    loanFolder: loan.loan_folder ?? null,
    loanStatus: loan.trans_details_loan_status ?? null,
    loanPurpose: loan.trans_details_loan_purpose ?? null,
    loanAmount: loan.trans_details_loan_amt ?? null,
    propertyAddress: loan.property_address ?? null,
    propertyCity: loan.subject_property_city ?? null,
    propertyState: loan.subject_property_state ?? null,
    loanOfficerName: [loan.loan_officer_first_name, loan.loan_officer_last_name].filter(Boolean).join(' ') || null
  };
}

export function ProspectDashboard({
  onViewDocuments,
  onOpenDataExports,
  canGenerateReports = false
}: ProspectDashboardProps = {}) {
  // Get organization ID from auth context
  const { user } = useAuth();
  const organizationId = isExtendedUser(user) ? user.organization_id : undefined;
  const loanScope = useLoanCenterScope();
  const authorizedOrganizationId = loanScope.canAccess
    ? organizationId
    : undefined;

  useEffect(() => {
    setLoanDataExportSelectionOwner(user?.id, organizationId);
  }, [organizationId, user?.id]);

  // State - default to showing only Active folder
  const [filters, setFilters] = useState<ProspectFilters>({
    loanFolder: 'all',
    loanOfficerId: 'all',
    loanPurpose: 'all',
    searchQuery: '',
    // Exclude all folders except Active by default
    excludedFolders: ['Test', '(Archive)', 'Transferred Data', 'Completed Loans', 'Adverse Loans', '(Trash)', 'Prospect'],
    excludedMilestones: [],
    excludedChannels: [],
    pipeline: null
  });

  const [sortConfig, setSortConfig] = useState<ProspectSortConfig>({
    field: 'creation_date',
    direction: 'desc'
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showLoanDetailsSheet, setShowLoanDetailsSheet] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [deepLinkedLoan, setDeepLinkedLoan] = useState<ProspectLoan | null>(null);
  const [detailsSheetInitialTab, setDetailsSheetInitialTab] = useState<LoanDetailsTab>('form1008');
  const pendingDeepLinkRef = useRef(parseLoanCenterDeepLink(window.location.search));

  const handleCloseLoanDetails = useCallback(() => {
    setShowLoanDetailsSheet(false);
    setSelectedLoanId(null);
    setDeepLinkedLoan(null);
  }, []);

  useEffect(() => {
    if (!loanScope.isScoped) {
      return;
    }
    setFilters((current) => current.loanOfficerId === 'all'
      ? current
      : { ...current, loanOfficerId: 'all' });
  }, [loanScope.isScoped]);

  useEffect(() => {
    if (loanScope.isLoading || loanScope.canAccess) {
      return;
    }
    setSelectedIds([]);
    clearLoanDataExportCandidates();
    handleCloseLoanDetails();
  }, [
    handleCloseLoanDetails,
    loanScope.canAccess,
    loanScope.isLoading
  ]);

  // Redesigned active Loan Center state. Direction is server-canonical
  // (userPreferencesService.loanCenterDirection). While unresolved, `direction`
  // is null and the redesign wrapper renders a skeleton for direction-specific
  // content — no flash-of-wrong-view.
  const { direction, setDirection, isResolved: isDirectionResolved } = useLoanCenterDirection();

  const handleDirectionChange = useCallback((next: LoanCenterDirection) => {
    // View-scoped selection: Pipeline/Hybrid don't expose row checkboxes, so
    // clear any Table-selected ids on direction change.
    setSelectedIds([]);
    setDirection(next);
  }, [setDirection]);

  // Snapshot of the active-workflow filter subset, captured when entering a
  // post-closing-like workflow so returning to All Loans restores the whole view
  // (folders, lock/milestone/channel, segment, LO, purpose) — not just folders.
  const savedActiveFiltersRef = useRef<Partial<ProspectFilters>>({});

  // Get available folders for filter
  const { folders: availableFolders } = useAvailableFolders(
    authorizedOrganizationId
  );

  // Fetch mode depends on the active redesign direction:
  //   table → paginated (existing infinite scroll)
  //   pipeline | hybrid → full (loop internally until totalCount reached, so column
  //     distributions and hybrid summaries are correct — see approved plan §3 rule).
  // While direction is unresolved, pass `undefined` organizationId so the hook
  // short-circuits and does not burn a paginated fetch that a pipeline-first
  // user would immediately replace.
  const isPostClosing = filters.pipeline === 'post_closing';
  const isMersAudit = filters.pipeline === 'mers_audit';
  // Both Post Closing and MERS Audit render via the legacy PostClosingTable path
  // (not the redesign direction views), so they share branch/gating logic.
  const isPostClosingLike = isPostClosing || isMersAudit;

  const fetchMode: FetchMode = direction === 'pipeline' || direction === 'hybrid' ? 'full' : 'paginated';
  // Post-closing-like paths don't use the redesign, so they shouldn't wait on loanCenterDirection.
  const dataOrganizationId = (
    isPostClosingLike || isDirectionResolved
  ) ? authorizedOrganizationId : undefined;

  const {
    prospects,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    loadMore,
    error,
    refetch,
    updateProspect
  } = usePaginatedProspects(
    dataOrganizationId,
    filters,
    sortConfig,
    false,
    fetchMode,
    {
      refetchOnWindowFocus: loanScope.isScoped,
      refreshIntervalMs: loanScope.isScoped
        ? (loanScope.context?.refreshIntervalSeconds ?? 60) * 1000
        : 0
    }
  );

  // Strip queueStage and segment from analytics filters — cards/segment-bar chips show
  // full-pipeline totals under the current non-segment filters. Clicking a segment chip must
  // not collapse the other chip counts (sibling facets, not a narrowing stack). Segment
  // changes must not retrigger the analytics RPC or the chips would blank out on every click.
  const analyticsFilters = useMemo(() => {
    // queueStage and segment are intentionally stripped from analytics filters:
    // cards/segment-bar chips show full-pipeline totals so the analytics RPC
    // must not re-trigger when those facet chips change.
    const { queueStage: _queueStage, segment: _segment, ...rest } = filters;
    return rest;
  }, [filters]);

  // Server-side analytics — disabled when in post-closing mode to avoid wasted RPC traffic.
  // Consume analyticsFilters (not filters) so segment changes don't invalidate the analytics
  // query — chips stay stable as sibling facets.
  const { analytics, isLoading: analyticsLoading } = useServerAnalytics(
    authorizedOrganizationId,
    analyticsFilters,
    false, // Don't include transferred data
    !isPostClosingLike // enabled — off for both post-closing and MERS audit
  );

  // Post-closing stage analytics — only active in post-closing mode
  const {
    stageAnalytics,
    isLoading: stageAnalyticsLoading,
    error: stageAnalyticsError,
    refetch: refetchStageAnalytics
  } = usePostClosingAnalytics(
    authorizedOrganizationId,
    analyticsFilters,
    isPostClosing
  );

  // Lookup current loan from prospects array by ID
  // This ensures the loan passed to LoanDetailsSheet stays current when real-time updates come in
  const selectedLoanForDetails = useMemo(
    () => selectedLoanId
      ? prospects.find(p => p.id === selectedLoanId) ??
        (deepLinkedLoan?.id === selectedLoanId ? deepLinkedLoan : null)
      : null,
    [deepLinkedLoan, prospects, selectedLoanId]
  );

  useEffect(() => {
    const deepLink = pendingDeepLinkRef.current;
    if (!deepLink || !authorizedOrganizationId) {
      return;
    }

    let cancelled = false;

    const openDeepLinkedLoan = async () => {
      const { data, error: fetchError } = await supabase
        .from('encompass_loans')
        .select('*')
        .eq('id', deepLink.loanId)
        .maybeSingle();

      if (cancelled) {
        return;
      }
      if (fetchError || !data) {
        pendingDeepLinkRef.current = null;
        log.warn('Unable to resolve Loan Center deep link', {
          metadata: {
            loanId: deepLink.loanId,
            error: fetchError?.message ?? 'Loan not found'
          }
        });
        toast.error('Unable to open the linked loan');
        return;
      }

      const loan = data as ProspectLoan;
      pendingDeepLinkRef.current = null;
      setDeepLinkedLoan(loan);
      setSelectedLoanId(loan.id);
      setDetailsSheetInitialTab(deepLink.tab);
      setShowLoanDetailsSheet(true);
    };

    void openDeepLinkedLoan();
    return () => {
      cancelled = true;
    };
  }, [authorizedOrganizationId]);

  const handlePipelineChange = useCallback((nextPipeline: 'post_closing' | 'mers_audit' | null) => {
    const currentPipeline = filters.pipeline ?? null;
    const wasPCLike = currentPipeline === 'post_closing' || currentPipeline === 'mers_audit';
    const isPCLike = nextPipeline === 'post_closing' || nextPipeline === 'mers_audit';

    // Snapshot the active-only filter subset when ENTERING a post-closing-like
    // workflow from active, and restore it only when LEAVING back to active.
    // Intra-workflow switches (post_closing <-> mers_audit) must not clobber it.
    if (isPCLike && !wasPCLike) {
      savedActiveFiltersRef.current = {
        excludedFolders: filters.excludedFolders || [],
        lockStatus: filters.lockStatus ?? null,
        excludedMilestones: filters.excludedMilestones || [],
        excludedChannels: filters.excludedChannels || [],
        segment: filters.segment ?? null,
        loanOfficerId: filters.loanOfficerId,
        loanPurpose: filters.loanPurpose
      };
      setSelectedIds([]);
    }

    // Default sort is set on EVERY transition so it always matches the target.
    if (nextPipeline === 'post_closing') {
      setSortConfig({ field: 'funded_date', direction: 'desc' });
    } else if (nextPipeline === 'mers_audit') {
      // Composite key surfaces missing-MERS purchased loans first (see RPC).
      setSortConfig({ field: 'mers_missing_first', direction: 'asc' });
    } else {
      setSortConfig({ field: 'creation_date', direction: 'desc' });
    }

    if (isPCLike) {
      setFilters(prev => ({
        ...prev,
        pipeline: nextPipeline,
        // MERS audit includes Completed Loans (where purchased loans live); post
        // closing uses the standard exclusions.
        excludedFolders: nextPipeline === 'mers_audit' ? MERS_AUDIT_EXCLUDED_FOLDERS : DEFAULT_EXCLUDED_FOLDERS,
        lockStatus: null,
        excludedMilestones: [],
        excludedChannels: [],
        deliveryStatus: null,
        queueStage: null,
        // Neutralize hidden active-only facets so they can't silently narrow the audit.
        segment: null,
        // MERS sub-filter is audit-only; reset on every workflow transition.
        mersStatus: null,
        ...(nextPipeline === 'mers_audit' ? { loanOfficerId: 'all', loanPurpose: 'all' } : {})
      }));
    } else {
      // Returning to All Loans: clear post-closing facets and restore the active
      // filter subset captured on entry (lossless round-trip), not just folders.
      setFilters(prev => ({
        ...prev,
        pipeline: null,
        deliveryStatus: null,
        queueStage: null,
        mersStatus: null,
        ...(wasPCLike ? savedActiveFiltersRef.current : {})
      }));
    }
  }, [
    filters.pipeline, filters.excludedFolders, filters.lockStatus,
    filters.excludedMilestones, filters.excludedChannels, filters.segment,
    filters.loanOfficerId, filters.loanPurpose
  ]);

  // Toggle folder exclusion
  const handleToggleFolder = useCallback((folder: string) => {
    setFilters(prev => {
      const excluded = prev.excludedFolders || [];
      if (excluded.includes(folder)) {
        // Remove from excluded (show this folder)
        return { ...prev, excludedFolders: excluded.filter(f => f !== folder) };
      } else {
        // Add to excluded (hide this folder)
        return { ...prev, excludedFolders: [...excluded, folder] };
      }
    });
  }, []);

  // Handlers
  const handleSort = useCallback((field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const handleRefresh = useCallback(async () => {
    toast.promise(refetch(), {
      loading: 'Refreshing prospects...',
      success: 'Prospects refreshed successfully',
      error: 'Failed to refresh prospects'
    });
  }, [refetch]);

  const handleExport = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];

    // MERS audit: export the COMPLETE result set (paged on the RPC's hasMore),
    // not just the rows scrolled into view, with MERS-specific columns.
    if (isMersAudit) {
      if (!authorizedOrganizationId) {
        toast.error('MERS audit export unavailable — no organization');
        return;
      }
      // Abort (do NOT download a partial file) if the full-result fetch fails — a
      // truncated audit looks complete but isn't, which is the dangerous case.
      let auditRows: ProspectLoan[];
      try {
        const pending = fetchAllProspects(
          authorizedOrganizationId,
          filters,
          sortConfig
        );
        toast.promise(pending, {
          loading: 'Preparing MERS audit export…',
          success: 'Export downloaded successfully',
          error: 'MERS audit export failed — nothing downloaded'
        });
        auditRows = await pending;
      } catch (err) {
        log.error('MERS audit export fetch failed', {}, err instanceof Error ? err : new Error(String(err)));
        return;
      }
      log.info('Exporting MERS audit to CSV');

      const headers = [
        'Loan Number', 'Borrower', 'Loan Amount', 'Investor',
        'Funded Date', 'Purchased Date', 'MERS Transfer Date', 'MERS Transferred By'
      ];
      const rows = auditRows.map(p => [
        p.trans_details_loan_number,
        [p.borrower_first_name, p.borrower_last_name].filter(Boolean).join(' '),
        p.trans_details_loan_amt,
        p.investor_name,
        csvDate(p.funded_date),
        csvDate(p.purchased_date),
        csvDate(p.mers_transferred_at),
        p.mers_transferred_by_name
      ]);
      const csvContent = [
        headers.map(csvCell).join(','),
        ...rows.map(row => row.map(csvCell).join(','))
      ].join('\n');
      downloadCsv(csvContent, `mers-transfer-audit-${today}.csv`);
      log.info('MERS audit CSV export completed');
      return;
    }

    if (!authorizedOrganizationId) {
      toast.error('Loan export unavailable — no authorized organization');
      return;
    }

    let exportRows: ProspectLoan[];
    try {
      const pending = fetchAllProspects(
        authorizedOrganizationId,
        filters,
        sortConfig
      );
      toast.promise(pending, {
        loading: 'Revalidating loans and preparing export…',
        success: 'Loan access revalidated',
        error: 'Loan export failed — nothing downloaded'
      });
      exportRows = await pending;
    } catch (err) {
      log.error(
        'Loan CSV export fetch failed',
        {},
        err instanceof Error ? err : new Error(String(err))
      );
      return;
    }

    log.info('Exporting prospects to CSV', { count: exportRows.length });

    const headers = [
      'Loan Number',
      'Borrower First Name',
      'Borrower Last Name',
      'Loan Amount',
      'Property Address',
      'Loan Purpose',
      'Loan Officer',
      'Creation Source',
      'Creation Date'
    ];

    const rows = exportRows.map(p => {
      // Compute loan officer full name
      const loanOfficerName = p.loan_officer_first_name && p.loan_officer_last_name
        ? `${p.loan_officer_first_name} ${p.loan_officer_last_name}`
        : 'Unassigned';

      return [
        p.trans_details_loan_number,
        p.borrower_first_name,
        p.borrower_last_name,
        p.trans_details_loan_amt,
        p.property_address,
        p.trans_details_loan_purpose,
        loanOfficerName,
        p.creation_source,
        p.creation_date
      ];
    });

    const csvContent = [
      headers.map(csvCell).join(','),
      ...rows.map(row => row.map(csvCell).join(','))
    ].join('\n');

    downloadCsv(csvContent, `prospect-loans-${today}.csv`);

    log.info('CSV export completed successfully');
    toast.success('Export downloaded successfully');
  }, [
    isMersAudit,
    authorizedOrganizationId,
    filters,
    sortConfig
  ]);

  const handleViewDetails = useCallback((prospect: ProspectLoan) => {
    log.info('View details requested', {
      prospectId: prospect.id,
      loanNumber: prospect.trans_details_loan_number
    });
    setSelectedLoanId(prospect.id);
    setDetailsSheetInitialTab('form1008');
    setShowLoanDetailsSheet(true);
  }, []);

  const handleViewDetailsPostClosing = useCallback((prospect: ProspectLoan) => {
    log.info('View post-closing details requested', {
      prospectId: prospect.id,
      loanNumber: prospect.trans_details_loan_number
    });
    setSelectedLoanId(prospect.id);
    setDetailsSheetInitialTab('delivery');
    setShowLoanDetailsSheet(true);
  }, []);

  const handleViewInvestorLock = useCallback(async (prospect: ProspectLoan) => {
    log.info('View investor lock requested', {
      prospectId: prospect.id,
      loanNumber: prospect.trans_details_loan_number,
      commitmentNumber: prospect.rate_lock_investor_commitment
    });

    try {
      // Fetch investor lock documents for this loan
      const docs = await getInvestorLockDocuments(prospect.encompass_loan_id);

      if (docs.length === 0) {
        toast.error('No investor lock document found');
        return;
      }

      // Get the most recent document
      const latestDoc = docs[0];

      if (!latestDoc.storageUrl) {
        toast.error('Document storage URL not found');
        return;
      }

      // Get download URL and open in new tab
      const url = await getDocumentDownloadUrl(latestDoc.storageUrl);
      window.open(url, '_blank');
    } catch (error) {
      log.error('Failed to open investor lock document', {}, error as Error);
      toast.error('Failed to open investor lock document');
    }
  }, []);

  const handleViewDocuments = useCallback((prospect: ProspectLoan) => {
    log.info('Documents requested', {
      prospectId: prospect.id,
      loanNumber: prospect.trans_details_loan_number,
      encompassLoanId: prospect.encompass_loan_id
    });

    if (onViewDocuments) {
      onViewDocuments(prospect);
      return;
    }

    // Open in new window - auth is shared via localStorage
    const url = `/internal/documents/${prospect.encompass_loan_id}`;
    window.open(url, '_blank', 'noopener');
  }, [onViewDocuments]);

  const handleOpenDataExports = useCallback((loans: ProspectLoan[] = []) => {
    if (loans.length > 0) {
      addLoanDataExportCandidates(loans.map(toExportCandidate));
    }
    onOpenDataExports?.();
  }, [onOpenDataExports]);

  const handleAssignLO = useCallback((prospectId: string) => {
    // TODO: Open assign LO dialog
    log.info('Loan officer assignment requested', { prospectId });
    toast.info('Loan officer assignment coming soon');
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      loadMore();
    }
  }, [loadMore, isLoadingMore, hasMore]);

  // Wrap updateProspect to also refetch stage analytics when in post-closing mode
  const handleProspectUpdate = useCallback((loanId: string, updates: Partial<ProspectLoan>) => {
    updateProspect(loanId, updates);
    if (isPostClosing) {
      refetchStageAnalytics(true);
    }
  }, [updateProspect, isPostClosing, refetchStageAnalytics]);

  // Card click → filter table by queue stage (toggle)
  const handleStageCardClick = useCallback((stageKey: PostClosingQueueStage | null) => {
    setSelectedIds([]);
    setFilters(prev => ({
      ...prev,
      queueStage: stageKey === null ? null : (prev.queueStage === stageKey ? null : stageKey),
      deliveryStatus: null
    }));
  }, []);

  if (loanScope.isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Verifying Loan Center access…
        </div>
      </div>
    );
  }

  if (
    loanScope.mode === 'unconfigured'
    || isEncompassLinkMissingError(loanScope.error)
    || isEncompassLinkMissingError(error)
  ) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-lg rounded-lg border bg-card p-6 text-center shadow-sm">
          <Link2Off className="mx-auto h-8 w-8 text-warning" />
          <h2 className="mt-3 text-lg font-semibold">
            Encompass account link required
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Your Lendwise account is not linked to an Encompass user. Ask an
            administrator to configure your Encompass user ID before opening
            Loan Center.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void loanScope.refresh()}
          >
            Check Again
          </Button>
        </div>
      </div>
    );
  }

  if (
    loanScope.mode === 'denied'
    || isLoanAccessDeniedError(loanScope.error)
    || isLoanAccessDeniedError(error)
  ) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-lg rounded-lg border bg-card p-6 text-center shadow-sm">
          <ShieldAlert className="mx-auto h-8 w-8 text-destructive" />
          <h2 className="mt-3 text-lg font-semibold">
            Loan Center access unavailable
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            You do not have access to this loan or to Loan Center. If a loan
            was reassigned, it has been removed from your view.
          </p>
        </div>
      </div>
    );
  }

  if (loanScope.error) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block rounded-lg border bg-card p-4">
          <p className="font-medium text-destructive">
            Failed to verify Loan Center access
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {loanScope.error.message}
          </p>
          <Button
            onClick={() => void loanScope.refresh()}
            className="mt-4"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Data error state
  if (error) {
    return (
      <div className="p-8 text-center">
        {/* Same card and semantic text tokens as the access error above; they meet AA contrast. */}
        <div className="inline-block rounded-lg border bg-card p-4">
          <p className="font-medium text-destructive">Failed to load prospects</p>
          <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
          <Button onClick={handleRefresh} className="mt-4">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-2 sm:p-6">
      {/* Header — the count and the controls wrap onto more lines when narrow */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="text-sm text-muted-foreground">
          {totalCount > 0 && (
            <span>
              {totalCount.toLocaleString()} {isMersAudit ? 'purchased loans' : isPostClosing ? 'post-closing loans' : 'active loans'}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Folder Filter Dropdown — hidden in post-closing & MERS audit (they manage
              their own folder exclusions) */}
          {!isPostClosingLike && availableFolders.length > 0 && (() => {
            // Only count excluded folders that actually exist in available folders
            const actualExcludedCount = filters.excludedFolders?.filter(f =>
              availableFolders.some(af => af.folder === f)
            ).length || 0;
            const visibleFolderCount = availableFolders.length - actualExcludedCount;

            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <FolderOpen className="h-4 w-4 mr-2" />
                    Folders
                    {actualExcludedCount > 0 && (
                      <span className="ml-1 text-muted-foreground">
                        ({visibleFolderCount}/{availableFolders.length})
                      </span>
                    )}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Show Folders</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {availableFolders.map(({ folder, count }) => {
                    const isIncluded = !filters.excludedFolders?.includes(folder);
                    return (
                      <DropdownMenuCheckboxItem
                        key={folder}
                        checked={isIncluded}
                        onCheckedChange={() => handleToggleFolder(folder)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {folder} ({count})
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                  {actualExcludedCount > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuCheckboxItem
                        checked={false}
                        onCheckedChange={() => setFilters(prev => ({ ...prev, excludedFolders: [] }))}
                        onSelect={(e) => e.preventDefault()}
                      >
                        Show all folders
                      </DropdownMenuCheckboxItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })()}
          {/* Direction tabs (active workflow only — hidden in post-closing & MERS audit) */}
          {!isPostClosingLike && (
            <DirectionTabs direction={direction} onChange={handleDirectionChange} />
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={prospects.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          {canGenerateReports && onOpenDataExports && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleOpenDataExports()}
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Data Exports
            </Button>
          )}
        </div>
      </div>

      {isPostClosingLike ? (
        <>
          {/* Stage cards are post-closing-only; the MERS audit has no stage buckets. */}
          {isPostClosing && (
            <PostClosingAnalyticsCards
              stageAnalytics={stageAnalytics}
              isLoading={stageAnalyticsLoading}
              error={stageAnalyticsError}
              selectedStage={filters.queueStage}
              onStageClick={handleStageCardClick}
            />
          )}

          <ProspectFiltersComponent
            filters={filters}
            onFiltersChange={setFilters}
            onPipelineChange={handlePipelineChange}
            organizationId={authorizedOrganizationId}
            hideLoanOfficerFilter={loanScope.isScoped}
          />

          <PostClosingTable
            prospects={prospects}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            totalCount={totalCount}
            sortConfig={sortConfig}
            onSort={handleSort}
            onViewDetails={handleViewDetailsPostClosing}
            onLoadMore={handleLoadMore}
            onLoanUpdate={handleProspectUpdate}
            mersAuditMode={isMersAudit}
          />
        </>
      ) : (
        <>
          <LoanCenterRedesign
            direction={direction}
            directionTabsSlot={null}
            toolbarSlot={
              <ProspectFiltersComponent
                filters={filters}
                onFiltersChange={setFilters}
                onPipelineChange={handlePipelineChange}
                organizationId={authorizedOrganizationId}
                hideLoanOfficerFilter={loanScope.isScoped}
              />
            }
            analytics={analytics}
            analyticsLoading={analyticsLoading}
            segment={filters.segment ?? 'all'}
            onSegmentChange={(s) => setFilters(prev => ({ ...prev, segment: s === 'all' ? null : s }))}
            prospects={prospects}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            totalCount={totalCount}
            sortConfig={sortConfig}
            onSort={handleSort}
            selectedIds={selectedIds}
            onSelectIds={setSelectedIds}
            onViewDetails={handleViewDetails}
            onViewDocuments={handleViewDocuments}
            onViewInvestorLock={handleViewInvestorLock}
            onAssignLO={handleAssignLO}
            onLoadMore={handleLoadMore}
          />

          {/* Selection banner — Table direction only. */}
          {selectedIds.length > 0 && direction === 'table' && (
            <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
              <p role="status" className="text-sm font-medium text-blue-900">
                {selectedIds.length} {selectedIds.length === 1 ? 'prospect' : 'prospects'} selected
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setSelectedIds([])}>
                  Clear Selection
                </Button>
                {canGenerateReports && onOpenDataExports && (
                  <Button
                    size="sm"
                    onClick={() => handleOpenDataExports(prospects.filter((prospect) => selectedIds.includes(prospect.id)))}
                  >
                    <FileSpreadsheet className="mr-2 h-4 w-4" />
                    Export Data Tape
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Loan Details Sheet */}
      {selectedLoanForDetails && (
        <LoanDetailsSheet
          isOpen={showLoanDetailsSheet}
          onClose={handleCloseLoanDetails}
          loan={selectedLoanForDetails}
          initialTab={detailsSheetInitialTab}
          onLoanUpdate={handleProspectUpdate}
          onActiveLockChanged={() => void refetch()}
          onOpenLoanDocuments={() => handleViewDocuments(selectedLoanForDetails)}
          onOpenDataExports={canGenerateReports && onOpenDataExports
            ? () => handleOpenDataExports([selectedLoanForDetails])
            : undefined}
        />
      )}
    </div>
  );
}
